import { CheckIcon } from '@heroicons/react/24/outline';
import React from 'react';

import type { FormCheckboxProps } from './forms.types';
import type { CheckboxState } from '@/types/forms';

const FormCheckbox = React.forwardRef<HTMLInputElement, FormCheckboxProps>(
  (
    {
      label,
      error,
      success,
      helpText,
      description,
      size = 'md',
      className = '',
      disabled = false,
      id,
      labelPosition = 'right',
      ...props
    },
    ref
  ) => {
    const reactId = React.useId();
    const checkboxId = id || `checkbox-${reactId}`;

    // Determine state
    const state: CheckboxState = error
      ? 'error'
      : success
        ? 'success'
        : 'default';

    const sizeClasses = {
      sm: 'h-3.5 w-3.5',
      md: 'h-4 w-4',
      lg: 'h-5 w-5',
    };

    /** The tick fills most of the box, as mobile's does (10/12/14px). */
    const checkSizeClasses = {
      sm: 'h-2.5 w-2.5',
      md: 'h-3 w-3',
      lg: 'h-3.5 w-3.5',
    };

    // The box is drawn, not native. A native checkbox takes its fill from the
    // browser's accent colour and its empty box from the browser's colour
    // scheme, and neither is Dō's. Empty, it is an outline in the state's
    // stroke over whatever surface it sits on; checked, it takes the selected
    // treatment (sakura wash + accent stroke) and a sakura tick, as mobile's
    // does. Sakura never fills it solid.
    const stateClasses = {
      default: 'border-stroke-default hover:not-checked:border-stroke-strong',
      error: 'border-error-500',
      success: 'border-success-500',
    };

    const labelStateClasses = {
      default: 'text-neutral-300',
      error: 'text-error-400',
      success: 'text-success-400',
    };

    const baseClasses =
      'peer shrink-0 appearance-none cursor-pointer rounded-sm border-2 bg-transparent focus:outline-none focus-visible:ring-state-focus transition-colors duration-normal checked:border-stroke-accent checked:bg-sakura-wash disabled:cursor-not-allowed disabled:opacity-60';
    const classes = [
      baseClasses,
      sizeClasses[size],
      stateClasses[state],
      className,
    ]
      .filter(Boolean)
      .join(' ');

    const labelSizeClasses = {
      sm: 'text-label-sm',
      md: 'text-label-sm',
      lg: 'text-body-md',
    };

    const checkboxElement = (
      <label
        htmlFor={checkboxId}
        className={`relative flex items-center justify-center min-w-11 min-h-11 -m-2.5 cursor-pointer ${
          disabled ? 'cursor-not-allowed' : ''
        }`}
      >
        <input
          ref={ref}
          id={checkboxId}
          type="checkbox"
          className={classes}
          disabled={disabled}
          aria-invalid={state === 'error'}
          {...props}
        />
        {/* Drawn heavier than the 1.5 default, which reads as a smudge this small */}
        <CheckIcon
          aria-hidden="true"
          strokeWidth={2.5}
          className={`pointer-events-none absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 text-primary-300 peer-checked:block peer-disabled:opacity-60 ${checkSizeClasses[size]}`}
        />
      </label>
    );

    const labelElement = (label || description) && (
      <div className={labelSizeClasses[size]}>
        {label && (
          // leading-6 matches the checkbox's visible 24px box (44px target
          // minus the -m-2.5 pull-in), so the first line centres on the box
          // instead of riding high in the top-aligned row.
          <label
            htmlFor={checkboxId}
            className={`font-medium leading-6 cursor-pointer ${labelStateClasses[state]} ${
              disabled ? 'opacity-60 cursor-not-allowed' : ''
            }`}
          >
            {label}
          </label>
        )}
        {description && (
          <p className="text-neutral-400 mt-0.5">{description}</p>
        )}
      </div>
    );

    return (
      <div className="space-y-1">
        <div
          className={`flex items-start gap-3 ${
            labelPosition === 'left' ? 'flex-row-reverse justify-end' : ''
          }`}
        >
          {checkboxElement}
          {labelElement}
        </div>

        {error && <p className="text-body-sm text-error-400 ml-7">{error}</p>}

        {helpText && !error && (
          <p className="text-label-xs text-neutral-400 ml-7">{helpText}</p>
        )}
      </div>
    );
  }
);

FormCheckbox.displayName = 'FormCheckbox';

export default FormCheckbox;
